import { ContainerImportPlan } from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import { ModuleDTO } from 'src/models/dto/ModuleDTO'
import { TemplateValidationError } from 'src/models/dto/TemplateValidationError'
import { CONTAINER_COLUMN } from 'src/services/import-content/TemplateSpreadsheetService'

export enum ImportStep {
    Upload = 0,
    Review = 1,
    Create = 2,
}

export const IMPORT_STEP_LABELS: Record<ImportStep, string> = {
    [ImportStep.Upload]: 'Upload',
    [ImportStep.Review]: 'Review',
    [ImportStep.Create]: 'Create',
}

export const ROWS_PER_PAGE = 10

const MAX_CELL_CHARS = 40

export const truncateCell = (value?: string): string =>
    value && value.length > MAX_CELL_CHARS ? `${value.slice(0, MAX_CELL_CHARS)}…` : value ?? ''

export const ROW_BORDER = { borderBottom: '1px solid #E1E4EA' }

export const pluralize = (count: number, noun: string): string =>
    `${count} ${noun}${count === 1 ? '' : 's'}`

export interface ParsedSpreadsheet {
    headers: string[]
    rowCount: number
    containerCount: number
    rows: Record<string, string>[]
    hasContainerColumn: boolean
}

// At most this many Item Container modules are created per import; above it the rows go straight
// into the item pool instead.
export const MAX_CONTAINERS_PER_IMPORT = 10

// Without a __container__ column, or with too many containers, the import can only skip containers.
export const mustSkipContainerCreation = (spreadsheet: ParsedSpreadsheet): boolean =>
    !spreadsheet.hasContainerColumn || spreadsheet.containerCount > MAX_CONTAINERS_PER_IMPORT

export interface SpreadsheetValidation {
    missing: string[]
    extra: string[]
}

export interface RowValidation {
    byRow: Record<number, string[]>
    general: string[]
}

const ROW_NUMBER = /^Row (\d+):\s*(.*)$/

const dedupe = (messages: string[]): string[] =>
    messages.filter((message, index) => messages.indexOf(message) === index)

export const FIRST_DATA_LINE = 2

// Each spreadsheet row becomes one copy of the template's pages, so this is the item count per row.
export const countTemplateItems = (template: ModuleDTO): number =>
    template.workflow.reduce(
        (count, activity) => count + (activity.moduleChild.items?.length ?? 0),
        0
    )

export interface ContainerGroup {
    name: string
    rowIndices: number[]
}

// Module names are case-insensitive, so values that differ only by case share a container, named
// as the group's first row writes it (the backend groups the same way).
export const groupRowsByContainer = (rows: Record<string, string>[]): ContainerGroup[] => {
    const groupsByKey = new Map<string, ContainerGroup>()
    rows.forEach((row, rowIndex) => {
        const name = (row[CONTAINER_COLUMN] ?? '').trim()
        const existing = groupsByKey.get(name.toLowerCase())
        if (existing) {
            existing.rowIndices.push(rowIndex)
        } else {
            groupsByKey.set(name.toLowerCase(), { name, rowIndices: [rowIndex] })
        }
    })
    return Array.from(groupsByKey.values())
}

// Container names match case-insensitively, so plans are looked up by lower-cased name.
export const plansByContainerName = (
    plans: ContainerImportPlan[]
): Record<string, ContainerImportPlan> =>
    plans.reduce<Record<string, ContainerImportPlan>>(
        (byName, plan) => ({ ...byName, [plan.containerName.toLowerCase()]: plan }),
        {}
    )

export interface PlannedContainer {
    name: string
    rowCount: number
    plan?: ContainerImportPlan
}

// The Item Container modules an import creates or updates, named as the import saves them: an
// existing module keeps its own name.
export const planContainers = (
    rows: Record<string, string>[],
    plansByContainer: Record<string, ContainerImportPlan>
): PlannedContainer[] =>
    groupRowsByContainer(rows).map(({ name, rowIndices }) => {
        const plan = plansByContainer[name.toLowerCase()]
        return { name: plan?.containerName ?? name, rowCount: rowIndices.length, plan }
    })

export enum ContainerImportAction {
    Create = 'create',
    Update = 'update',
    NewVersion = 'newVersion',
}

// An import overwrites a draft in place and creates a new draft version of a module under review
// or later, so the existing version's status decides what happens to it.
export const containerImportAction = (plan?: ContainerImportPlan): ContainerImportAction => {
    if (!plan?.existingModuleVersionId) {
        return ContainerImportAction.Create
    }
    return plan.existingModuleStatus?.startsWith('DRAFT')
        ? ContainerImportAction.Update
        : ContainerImportAction.NewVersion
}

export const FLAT_VALIDATION_BATCH_SIZE = 50

// Without containers there is nothing to group by, so the rows are validated in fixed-size batches.
export const validationGroups = (
    spreadsheet: ParsedSpreadsheet,
    skipContainerCreation: boolean
): number[][] => {
    if (!skipContainerCreation) {
        return groupRowsByContainer(spreadsheet.rows).map(({ rowIndices }) => rowIndices)
    }
    const groups: number[][] = []
    for (let start = 0; start < spreadsheet.rows.length; start += FLAT_VALIDATION_BATCH_SIZE) {
        groups.push(
            Array.from(
                { length: Math.min(FLAT_VALIDATION_BATCH_SIZE, spreadsheet.rows.length - start) },
                (_unused, offset) => start + offset
            )
        )
    }
    return groups
}

const PROGRESS = /(\d+) of (\d+)/

// The backend reports an item pool save as e.g. "DATABASE:PROCESSING 3 of 10 [1]".
export const parseProgress = (message: string): { done: number; total: number } | undefined => {
    const match = PROGRESS.exec(message)
    const total = match ? Number(match[2]) : 0
    return match && total > 0 ? { done: Math.min(Number(match[1]), total), total } : undefined
}

const mapErrorsToRows = (
    errors: TemplateValidationError[],
    rowIndexOf: (rowNumber: number) => number | undefined
): RowValidation => {
    const byRow: Record<number, string[]> = {}
    const general: string[] = []

    errors.forEach(({ message }) => {
        const match = ROW_NUMBER.exec(message)
        const rowIndex = match ? rowIndexOf(Number(match[1])) : undefined
        if (match && rowIndex !== undefined) {
            byRow[rowIndex] = dedupe([...(byRow[rowIndex] ?? []), match[2]])
        } else if (message) {
            general.push(message)
        }
    })

    return { byRow, general }
}

// A batch validated on its own numbers its rows by their position in the request.
export const mapContainerErrorsToRows = (
    errors: TemplateValidationError[],
    rowIndices: number[]
): RowValidation => mapErrorsToRows(errors, (rowNumber) => rowIndices[rowNumber - 1])

// A whole-file validation numbers its rows by spreadsheet line, as the review table does.
export const mapSpreadsheetErrorsToRows = (
    errors: TemplateValidationError[],
    rowCount: number
): RowValidation =>
    mapErrorsToRows(errors, (line) => {
        const rowIndex = line - FIRST_DATA_LINE
        return rowIndex >= 0 && rowIndex < rowCount ? rowIndex : undefined
    })

export const CONTAINER_VALIDATION_CONCURRENCY = 4

export const CONTAINER_VALIDATION_STAGGER_MS = 200

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const validateContainers = async (
    groups: number[][],
    validateGroup: (rowIndices: number[]) => Promise<RowValidation>,
    onRowsValidated: (completed: number) => void,
    concurrency: number = CONTAINER_VALIDATION_CONCURRENCY,
    staggerMs: number = CONTAINER_VALIDATION_STAGGER_MS
): Promise<RowValidation> => {
    const byRow: Record<number, string[]> = {}
    const general: string[] = []
    let nextIndex = 0
    let completed = 0

    const worker = async (workerIndex: number) => {
        if (workerIndex > 0) {
            await delay(workerIndex * staggerMs)
        }
        while (nextIndex < groups.length) {
            const rowIndices = groups[nextIndex++]
            try {
                const validated = await validateGroup(rowIndices)
                Object.assign(byRow, validated.byRow)
                general.push(...validated.general)
            } catch (e) {
                const message =
                    e instanceof Error ? e.message : 'These rows could not be validated.'
                rowIndices.forEach((rowIndex) => {
                    byRow[rowIndex] = [message]
                })
            }
            completed += rowIndices.length
            onRowsValidated(completed)
        }
    }

    await Promise.all(
        Array.from({ length: Math.min(concurrency, groups.length) }, (_unused, workerIndex) =>
            worker(workerIndex)
        )
    )

    return { byRow, general: dedupe(general) }
}

export const validateSpreadsheet = (
    spreadsheet: ParsedSpreadsheet,
    placeholders: string[]
): SpreadsheetValidation => {
    const headerSet = new Set(spreadsheet.headers)
    const placeholderSet = new Set(placeholders)

    return {
        missing: placeholders.filter((name) => name !== CONTAINER_COLUMN && !headerSet.has(name)),
        extra: spreadsheet.headers.filter((header) => !placeholderSet.has(header)),
    }
}
